import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  deleteLogo,
  fetchBranding,
  updateBranding,
  uploadLogo,
  type Brand,
  type Branding,
} from '@/api/branding';
import type { ApiError } from '@/api/http';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { hexToHslChannels } from '@/theme/color';
import { whiteLabel } from '@/theme/tokens';
import { type } from '@/theme/typography';

import { accentIsUsable, accentRatio, isHex } from './accentField';

/** The server's bound, in the sentence the field states **before** a file is chosen. */
const MAX_LOGO_LABEL = '512 KB';

/**
 * §6.10's **branding** half: the accent and the logo (backend TASK-096, TASK-098).
 *
 * ⚠️ **The accent is refused before the request, by the same rule the server refuses it with.** §2:
 * *"an unreadable primary button is not a branding preference"* — a **product rule**, so the server is
 * authoritative and this is a kindness: a preview that says *why*, with the measured ratio, beats a 422
 * after a save. The two implementations are pinned to one fixture table whose sha256 both suites assert
 * (`theme/accentContrast.test.ts`, `backend/tests/test_accent_contrast.py`), because one rule in two
 * languages is the drift this project keeps paying for.
 *
 * ⚠️ **The preview renders the colour on the page it will actually be read on**, not on a swatch: a
 * filled button and a link, both against `paper-bg`, which is exactly what the rule measures. A swatch
 * shows a colour; this shows the decision.
 *
 * ⚠️ **The logo's bounds are stated before a file is chosen**, not after a refusal — the format and the
 * size, in the field's own description. The server decides from the **bytes** regardless (it refuses an
 * SVG by name, because an SVG is a document that can carry script and would be served from this
 * origin), so the sentence is guidance and the 422 is the gate.
 *
 * ⚠️ **Which is why the refusal below renders the server's own `detail` and never a sentence of this
 * screen's.** The hint states two bounds; since BUG-061 (2026-08-09) the server walks the whole PNG /
 * JPEG container rather than its first bytes, and a **third** refusal came with that — a real,
 * viewable image with padding or an EXIF trailer after `IEND` / `EOI` is refused, and the answer a
 * reader needs is *"export it again"* rather than *"choose a PNG"*. Enumerating the bounds here as
 * though the hint were the whole rule is what would go stale; `apiUpload`'s docblock in `api/http.ts`
 * carries the full statement of what the walk proves and what it now costs.
 */
export function BrandingSection() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const branding = useQuery<Branding, ApiError>({
    queryKey: queryKeys.branding(),
    queryFn: fetchBranding,
  });

  const stored = branding.data?.tenant?.accent ?? null;
  const logoUrl = branding.data?.tenant?.logo_url ?? null;

  const [draft, setDraft] = useState<string | null>(null);
  const accent = draft ?? stored ?? '';
  const fileInput = useRef<HTMLInputElement>(null);

  const save = useMutation<Brand, ApiError, string | null>({
    mutationFn: (value) => updateBranding({ accent: value }),
    onSuccess: () => {
      setDraft(null);
      // One key, one reader: the shell owns applying these three values (TASK-102), so invalidating
      // here is what repaints the whole app rather than this section.
      queryClient.invalidateQueries({ queryKey: queryKeys.branding() });
    },
  });

  const logo = useMutation<Brand, ApiError, File>({
    mutationFn: (file) => uploadLogo(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.branding() }),
  });

  const removeLogo = useMutation<Brand, ApiError, void>({
    mutationFn: () => deleteLogo(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.branding() }),
  });

  /**
   * The client's half of §2's rule, in three answers: nothing typed, not a colour, or unreadable —
   * with the **measured** ratio in the last one, because *"choose another colour"* with no number
   * sends a reader guessing.
   */
  const problem = (): string | undefined => {
    if (accent.trim() === '') return undefined;
    if (!isHex(accent)) return t('branding.notAColour');
    if (!accentIsUsable(accent)) {
      return t('branding.unreadable', {
        ratio: accentRatio(accent),
        threshold: 4.5,
      });
    }
    return undefined;
  };

  const refusal = problem();
  const previewable = accent.trim() !== '' && isHex(accent) && refusal === undefined;

  return (
    <section className="mt-10">
      <Eyebrow>{t('branding.eyebrow')}</Eyebrow>
      <p className={cn(type.body, 'mt-2 max-w-[68ch]')}>{t('branding.lead')}</p>

      {branding.isPending ? (
        <Skeleton className="mt-4 h-24 w-full max-w-md" />
      ) : (
        <div className="mt-4 max-w-md space-y-6">
          <div className="space-y-2">
            <Field
              label={t('branding.accent')}
              description={t('branding.accentHint')}
              error={
                refusal ?? (save.isError ? (save.error.detail ?? t('branding.failed')) : undefined)
              }
            >
              {(field) => (
                <Input
                  {...field}
                  value={accent}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder={whiteLabel.accent}
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
            </Field>

            {stored === null && draft === null && (
              <p className={type.metadata}>{t('branding.accentDefault')}</p>
            )}

            {/*
             * The preview, and it is only rendered for a colour that would be **accepted**: showing an
             * unreadable button next to the sentence explaining that it is unreadable would be an
             * illustration of the problem where a reader expects a result.
             */}
            {previewable && (
              <div
                className="rounded border border-rule bg-paper-bg p-3"
                style={{ ['--accent' as string]: hexToHslChannels(accent.trim()) }}
                data-testid="accent-preview"
              >
                <p className={cn(type.metadata, 'mb-2')}>{t('branding.preview')}</p>
                <div className="flex items-center gap-4">
                  <Button size="sm" variant="primary" type="button">
                    {t('branding.previewButton')}
                  </Button>
                  <span className="text-sm font-medium text-accent underline">
                    {t('branding.previewLink')}
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                size="sm"
                onClick={() => save.mutate(accent.trim())}
                disabled={refusal !== undefined || accent.trim() === '' || save.isPending}
              >
                {t('branding.save')}
              </Button>
              {stored !== null && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDraft(null);
                    save.mutate(null);
                  }}
                  disabled={save.isPending}
                >
                  {t('branding.reset')}
                </Button>
              )}
              {save.isSuccess && draft === null && (
                <span className={type.metadata}>{t('branding.saved')}</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-ink-700">{t('branding.logo')}</p>
            {logoUrl === null ? (
              <p className={type.metadata}>{t('branding.logoNone')}</p>
            ) : (
              <img
                src={logoUrl}
                alt={t('branding.logoAlt')}
                className="h-10 w-auto max-w-[200px] object-contain"
              />
            )}
            <p className="text-xs text-ink-700">
              {t('branding.logoHint', { max: MAX_LOGO_LABEL })}
            </p>

            {/*
             * A real `<input type="file">`, hidden and driven by a button: §4 has no file control, and
             * the native one cannot be styled consistently. It keeps its own label, so a screen reader
             * announces a file input rather than a button that mysteriously opens a picker.
             */}
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg"
              className="sr-only"
              aria-label={t('branding.choose')}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) logo.mutate(file);
                event.target.value = '';
              }}
            />
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                onClick={() => fileInput.current?.click()}
                disabled={logo.isPending}
              >
                {logo.isPending
                  ? t('branding.uploading')
                  : logoUrl === null
                    ? t('branding.choose')
                    : t('branding.replace')}
              </Button>
              {logoUrl !== null && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeLogo.mutate()}
                  disabled={removeLogo.isPending}
                >
                  {t('branding.remove')}
                </Button>
              )}
            </div>
            {(logo.isError || removeLogo.isError) && (
              <p className="text-xs text-negative">
                {logo.error?.detail ?? removeLogo.error?.detail ?? t('branding.logoFailed')}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
